import React from "react";

export function toYmd(d) {
  const x = new Date(d);
  const p = (n) => String(n).padStart(2, "0");
  return `${x.getFullYear()}-${p(x.getMonth() + 1)}-${p(x.getDate())}`;
}

export function addDays(d, days) {
  const copy = new Date(d);
  copy.setDate(copy.getDate() + days);
  return copy;
}

function startOfWeekMonday(d) {
  const copy = new Date(d);
  copy.setHours(12, 0, 0, 0);
  const day = copy.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  copy.setDate(copy.getDate() + diff);
  return copy;
}

export function startOfNextWeek(d = new Date()) {
  const monday = startOfWeekMonday(d);
  monday.setDate(monday.getDate() + 7);
  return monday;
}

export const DUE_DATE_PRESETS = [
  { id: "today", labelKey: "todo.dateToday" },
  { id: "tomorrow", labelKey: "todo.dateTomorrow" },
  { id: "next_week", labelKey: "todo.dateNextWeek" },
  { id: "in_three_days", labelKey: "todo.dateInThreeDays" },
];

export const REMINDER_PRESETS = [
  { id: "in_one_hour", labelKey: "todo.reminderInOneHour" },
  { id: "this_evening", labelKey: "todo.reminderThisEvening" },
  { id: "tomorrow_morning", labelKey: "todo.reminderTomorrowMorning" },
  { id: "next_week_morning", labelKey: "todo.reminderNextWeekMorning" },
];

export function resolveDueDatePreset(id) {
  const base = new Date();
  base.setHours(12, 0, 0, 0);
  switch (id) {
    case "today":
      return toYmd(base);
    case "tomorrow":
      return toYmd(addDays(base, 1));
    case "next_week":
      return toYmd(startOfNextWeek(base));
    case "in_three_days":
      return toYmd(addDays(base, 3));
    default:
      return null;
  }
}

export function resolveReminderPreset(id) {
  const now = new Date();
  switch (id) {
    case "in_one_hour":
      return new Date(now.getTime() + 60 * 60 * 1000).toISOString();
    case "this_evening": {
      const d = new Date(now);
      d.setSeconds(0, 0);
      d.setHours(17, 0, 0, 0);
      if (d <= now) {
        d.setDate(d.getDate() + 1);
        d.setHours(17, 0, 0, 0);
      }
      return d.toISOString();
    }
    case "tomorrow_morning": {
      const d = addDays(now, 1);
      d.setHours(9, 0, 0, 0);
      return d.toISOString();
    }
    case "next_week_morning": {
      const d = startOfNextWeek(now);
      d.setHours(9, 0, 0, 0);
      return d.toISOString();
    }
    default:
      return null;
  }
}

function reminderMatchesPreset(value, presetId) {
  if (!value) return false;
  const resolved = resolveReminderPreset(presetId);
  if (!resolved) return false;
  const a = new Date(value);
  const b = new Date(resolved);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return false;
  return Math.abs(a.getTime() - b.getTime()) < 60 * 1000;
}

export function dueDateMatchesPreset(value, presetId) {
  if (!value) return false;
  return value === resolveDueDatePreset(presetId);
}

export function reminderMatchesPresetId(value, presetId) {
  return reminderMatchesPreset(value, presetId);
}

export function formatSmartDueDate(value, t) {
  if (!value) return "";
  const d = new Date(`${value}T12:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  if (dueDateMatchesPreset(value, "today")) return t("todo.dateToday");
  if (dueDateMatchesPreset(value, "tomorrow")) return t("todo.dateTomorrow");
  if (dueDateMatchesPreset(value, "next_week")) return t("todo.dateNextWeek");
  if (dueDateMatchesPreset(value, "in_three_days")) return t("todo.dateInThreeDays");
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

export function formatSmartReminder(value, t) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  for (const preset of REMINDER_PRESETS) {
    if (reminderMatchesPresetId(value, preset.id)) return t(preset.labelKey);
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const thatDay = new Date(d);
  thatDay.setHours(0, 0, 0, 0);
  const dayDiff = Math.round((thatDay - today) / 86400000);
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  if (dayDiff === 0) return `${t("todo.dateToday")}, ${time}`;
  if (dayDiff === 1) return `${t("todo.dateTomorrow")}, ${time}`;
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function toDatetimeLocalValue(isoOrLocal) {
  if (!isoOrLocal) return "";
  const d = new Date(isoOrLocal);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function DateQuickPicker({ mode = "date", value, onChange, onClear, t }) {
  const presets = mode === "datetime" ? REMINDER_PRESETS : DUE_DATE_PRESETS;

  const isActive = (presetId) => {
    if (mode === "datetime") return reminderMatchesPresetId(value, presetId);
    return dueDateMatchesPreset(value, presetId);
  };

  const pickPreset = (presetId) => {
    const next =
      mode === "datetime" ? resolveReminderPreset(presetId) : resolveDueDatePreset(presetId);
    onChange?.(next);
  };

  return (
    <div className="td-date-picker">
      <div className="td-date-picker-presets">
        {presets.map((preset) => (
          <button
            key={preset.id}
            type="button"
            className={`td-popover-item ${isActive(preset.id) ? "is-active" : ""}`}
            onClick={() => pickPreset(preset.id)}
          >
            <span className="td-date-picker-preset-icon">{mode === "datetime" ? "◔" : "◷"}</span>
            <span>{t(preset.labelKey)}</span>
            {isActive(preset.id) ? <span className="td-popover-check">✓</span> : null}
          </button>
        ))}
      </div>

      <div className="td-date-picker-custom">
        <span className="td-date-picker-custom-label">{t("todo.dateCustom")}</span>
        <input
          type={mode === "datetime" ? "datetime-local" : "date"}
          className="td-popover-input"
          value={mode === "datetime" ? toDatetimeLocalValue(value) : value || ""}
          onChange={(e) => {
            const v = e.target.value;
            if (!v) {
              onChange?.(null);
              return;
            }
            if (mode === "datetime") {
              onChange?.(new Date(v).toISOString());
            } else {
              onChange?.(v);
            }
          }}
        />
      </div>

      {value && onClear ? (
        <button type="button" className="td-date-picker-clear" onClick={() => onClear()}>
          <span aria-hidden="true">×</span>
          <span>{t("todo.clear")}</span>
        </button>
      ) : null}
    </div>
  );
}
